// cookie-consent.service.ts
import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { BehaviorSubject, Observable, Subject } from 'rxjs';

export interface CookieConsent {
  necessaryConsent: boolean;
  analyticsConsent: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class CookieConsentService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly COOKIE_NAME = 'cookie_consent';
  private readonly COOKIE_EXPIRY_DAYS = 365;
  private readonly GA_MEASUREMENT_ID = 'G-NEHRMBYF7D';

  private consentSubject = new BehaviorSubject<CookieConsent | null>(null);
  public consent$: Observable<CookieConsent | null> =
    this.consentSubject.asObservable();

  private openSettingsSubject = new Subject<void>();
  public openSettings$: Observable<void> =
    this.openSettingsSubject.asObservable();

  constructor() {
    this.loadConsent();
  }

  private loadConsent(): void {
    if (isPlatformBrowser(this.platformId)) {
      const consent = this.getConsent();
      this.consentSubject.next(consent);

      // Initialize Google Analytics if consent was previously given
      if (consent?.analyticsConsent) {
        this.initializeGoogleAnalytics();
      }
    }
  }

  openSettings(): void {
    this.openSettingsSubject.next();
  }

  hasConsent(): boolean {
    return this.getConsent() !== null;
  }

  getConsent(): CookieConsent | null {
    if (!isPlatformBrowser(this.platformId)) {
      return null;
    }

    const cookieValue = this.getCookie(this.COOKIE_NAME);

    if (!cookieValue) {
      return null;
    }

    try {
      return JSON.parse(cookieValue) as CookieConsent;
    } catch (error) {
      console.error('Failed to parse cookie consent:', error);
      return null;
    }
  }

  saveConsent(preferences: CookieConsent): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const previousConsent = this.getConsent();
    const cookieValue = JSON.stringify(preferences);
    this.setCookie(this.COOKIE_NAME, cookieValue, this.COOKIE_EXPIRY_DAYS);
    this.consentSubject.next(preferences);

    if (preferences.analyticsConsent) {
      this.initializeGoogleAnalytics();
    } else if (
      previousConsent?.analyticsConsent &&
      !preferences.analyticsConsent
    ) {
      this.removeGoogleAnalyticsCookies();
    }
  }

  hasAnalyticsConsent(): boolean {
    const consent = this.getConsent();
    return consent?.analyticsConsent === true;
  }

  private initializeGoogleAnalytics(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    if (
      document.querySelector(`script[data-ga-id="${this.GA_MEASUREMENT_ID}"]`)
    ) {
      return;
    }

    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${this.GA_MEASUREMENT_ID}`;
    script.setAttribute('data-ga-id', this.GA_MEASUREMENT_ID);
    document.head.appendChild(script);

    // Initialize dataLayer
    (window as any).dataLayer = (window as any).dataLayer || [];
    function gtag(...args: any[]) {
      (window as any).dataLayer.push(arguments);
    }
    (window as any).gtag = gtag;

    gtag('js', new Date());
    gtag('config', this.GA_MEASUREMENT_ID);
  }

  private setCookie(name: string, value: string, days: number): void {
    const date = new Date();
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
    const expires = `expires=${date.toUTCString()}`;
    document.cookie = `${name}=${value};${expires};path=/;SameSite=Lax`;
  }

  private getCookie(name: string): string | null {
    const nameEQ = name + '=';
    const ca = document.cookie.split(';');

    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') {
        c = c.substring(1, c.length);
      }
      if (c.indexOf(nameEQ) === 0) {
        return c.substring(nameEQ.length, c.length);
      }
    }

    return null;
  }

  deleteCookie(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    document.cookie = `${this.COOKIE_NAME}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;`;
    this.consentSubject.next(null);
  }

  private removeGoogleAnalyticsCookies(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const cookies = document.cookie.split(';');

    // List of GA cookie patterns to remove
    const gaCookiePatterns = [
      '_ga',
      '_gid',
      '_gat',
      `_ga_${this.GA_MEASUREMENT_ID.replace('G-', '')}`,
    ];

    cookies.forEach((cookie) => {
      const cookieName = cookie.split('=')[0].trim();

      const isGACookie = gaCookiePatterns.some((pattern) =>
        cookieName.startsWith(pattern)
      );

      if (isGACookie) {
        document.cookie = `${cookieName}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;`;

        const domain = window.location.hostname;
        document.cookie = `${cookieName}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;domain=${domain};`;
        document.cookie = `${cookieName}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;domain=.${domain};`;
      }
    });
  }
}
